<template>
  <!-- Submit PerfectMatch on the RQ System Summary page (Android WebViewFragment bottom buttons):
       step 1 = Additional Items (OrderAddAddtionalItemsFragment), step 2 = Finalize Your Quote (FinalizeYourQuotationFragment)
       → payment type → orders/inspection/submit. The ✕ / the New Unit price close it (back to the summary page). -->
  <div ref="modal" class="modal fade" tabindex="-1" aria-hidden="true">
    <div
      class="modal-dialog modal-dialog-centered mobile:!w-full mobile:!right-0 mobile:!px-3 tablet:px-0 tablet:!w-[450px] !absolute tablet:!right-4 !top-0 !m-0 h-full">
      <div class="modal-content">
        <div class="modal-body !p-3 !pb-0 font-roboto flex flex-col">
          <FxModalHeader :title="step === 1 ? 'Additional Items' : 'Finalize Your Quote'" :dismiss="false" @close="close" />

          <div class="flex-1 flex flex-col px-[6px]">
            <template v-if="step === 1">
              <div class="flex justify-end mt-[10px]">
                <button type="button" class="fxBtnSecondary" @click="items.push({ id: 0, title: '', price: '', quantity: 1 })">+ Add Item</button>
              </div>
              <p v-if="!items.length" class="m-0 mt-[10px] text-[13px] text-[#999999]">No Additional Items to display</p>
              <!-- Android new_system_add_addtional_item: Title, Price, Quantity, Total Price, delete -->
              <div v-for="(it, idx) in items" :key="'f' + idx" class="rqFinalSection mt-[10px] flex flex-col gap-[8px]">
                <div>
                  <label class="rqLabel" :for="'rqItemTitle' + idx">Title <span class="text-[#EE5851]">*</span></label>
                  <input :id="'rqItemTitle' + idx" v-model="it.title" type="text" maxlength="120" placeholder="Enter Title"
                    class="rqInput" :class="err(idx, 'title') && 'rqInputError'" @input="clearErr(idx, 'title')">
                  <p v-if="err(idx, 'title')" class="rqFieldError">{{ err(idx, 'title') }}</p>
                </div>
                <div class="grid grid-cols-2 gap-[8px]">
                  <div>
                    <label class="rqLabel" :for="'rqItemPrice' + idx">Price <span class="text-[#EE5851]">*</span></label>
                    <input :id="'rqItemPrice' + idx" v-model="it.price" type="text" inputmode="decimal" placeholder="Enter Price"
                      class="rqInput" :class="err(idx, 'price') && 'rqInputError'" @input="clearErr(idx, 'price')">
                    <p v-if="err(idx, 'price')" class="rqFieldError">{{ err(idx, 'price') }}</p>
                  </div>
                  <div>
                    <label class="rqLabel" :for="'rqItemQty' + idx">Quantity <span class="text-[#EE5851]">*</span></label>
                    <input :id="'rqItemQty' + idx" v-model.number="it.quantity" type="number" min="1" max="99" placeholder="Enter Quantity"
                      class="rqInput" :class="err(idx, 'quantity') && 'rqInputError'" @input="clearErr(idx, 'quantity')">
                    <p v-if="err(idx, 'quantity')" class="rqFieldError">{{ err(idx, 'quantity') }}</p>
                  </div>
                </div>
                <div class="flex items-center justify-between gap-2">
                  <span class="text-[13px] text-[#333333]">Total Price: <span class="font-medium">{{
                    money(Number(it.price || 0) * Number(it.quantity || 0)) }}</span></span>
                  <button type="button" aria-label="Remove item"
                    class="w-[32px] h-[32px] rounded-full border-1 border-[#F67280] bg-[#fff] text-[#F67280] flex items-center justify-center hover:bg-[#F67280] hover:text-[#fff] transition"
                    @click="removeItem(idx)"><i class="fa fa-trash" /></button>
                </div>
              </div>
            </template>

            <template v-else>
              <div class="mt-[10px] flex flex-col gap-[12px]">
                <p class="m-0 text-[14px] text-[#666666]">Here are the details for the services you have selected</p>
                <div class="rqFinalSection">
                  <p class="rqFinalTitle">New Unit</p>
                  <div class="flex items-center justify-between gap-3">
                    <span class="text-[14px] text-[#333333] capitalize">{{ order.type || '-' }}</span>
                    <span class="text-[15px] font-medium text-[#333333]">{{ money(order.total - (order.preferred_payment_discount || 0)) }}</span>
                  </div>
                </div>
                <div class="rqFinalSection">
                  <p class="rqFinalTitle">Additional Items</p>
                  <div class="flex items-center justify-between gap-3">
                    <span class="text-[14px] text-[#333333]">{{ items.length ? items.length +
                      (items.length === 1 ? ' Additional Item' : ' Additional Items') : 'No Additional Items added' }}</span>
                    <button type="button" class="rqFinalPrice" @click="editItemsFromFinalize">{{ money(order.parts_total) }}</button>
                  </div>
                </div>
                <div class="flex items-center justify-between gap-2 px-[15px] py-[12px] rounded-[8px] bg-[#F3F4F7]">
                  <span class="text-[14px] font-medium text-[#333333]">Total Amount:</span>
                  <span class="text-[18px] font-bold text-[#0E2F7E]">{{ money(quoteTotal) }}</span>
                </div>
              </div>
              <template v-if="!skipPayment">
                <p class="m-0 mt-[15px] text-[15px] font-medium text-[#333333]">Payment Type</p>
                <p v-if="!paymentOptions.length" class="m-0 mt-[8px] text-[13px] text-[#999999]">Loading payment types…</p>
                <label v-for="opt in paymentOptions" :key="opt.key"
                  class="flex items-center justify-between gap-3 mt-[8px] px-[12px] py-[10px] rounded-[8px] border-1 cursor-pointer text-[13px]"
                  :class="payKey === opt.key ? 'border-[#7695FF] bg-[#EEF2FF]' : 'border-[#E2E5EC] bg-white'">
                  <span class="flex items-center gap-[8px]"><input v-model="payKey" type="radio" :value="opt.key"> {{ opt.label }}</span>
                  <span class="font-medium">{{ money(opt.amount) }}</span>
                </label>
              </template>
            </template>
          </div>

          <div class="uqcFooter flex items-center justify-end gap-[10px]">
            <!-- opened from Finalize: back to it without saving -->
            <button v-if="step === 1 && backToFinalize" type="button" class="uqcBtnCancel" @click="backFromItems">Back</button>
            <button v-if="step === 1" type="button" class="uqcBtnNext" :disabled="busy" @click="saveItems">Save Items</button>
            <button v-else type="button" class="uqcBtnNext" :disabled="busy || (!skipPayment && !payKey)" @click="submit">Complete Replacement Quote</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from "bootstrap";
import { useToast } from "vue-toastification";
import FxModalHeader from "@/components/nest_components/FxModalHeader.vue";
import { rqApi } from "@/services/rqApi";
import { disableScroll, enableScroll } from "@/helpers/scrollHelper";
const toast = useToast();

export default {
  name: "RqSubmitModal",
  components: { FxModalHeader },
  props: {
    orderId: { type: [String, Number], required: true },
  },
  // saved: items changed (refresh the summary); submitted: the report went in (the API returns the order)
  emits: ["saved", "submitted"],
  data() {
    return {
      step: 1,
      order: {},
      items: [],
      // per row: { title, price, quantity } messages (Save Items)
      itemErrors: [],
      paymentTypes: [],
      payKey: "",
      skipPayment: false,
      // Additional Items opened from Finalize: Save Items comes back to Finalize instead of closing
      backToFinalize: false,
      busy: false,
    };
  },
  computed: {
    quoteTotal() {
      const o = this.order || {};
      return Number(o.total || 0) + Number(o.parts_total || 0) - Number(o.preferred_payment_discount || 0);
    },
    // payment types; "other" expands into its sub types (cash / check / finance)
    paymentOptions() {
      const out = [];
      (this.paymentTypes || []).forEach(p => {
        if (Array.isArray(p.sub_type) && p.sub_type.length) {
          p.sub_type.forEach(sub => out.push({ key: `${p.type}|${sub.type}`, type: p.type, subType: sub.type, label: sub.display_name, amount: sub.amount }));
        } else if (p.type) {
          out.push({ key: `${p.type}|`, type: p.type, subType: null, label: p.display_name, amount: p.amount });
        }
      });
      return out;
    },
  },
  beforeUnmount() {
    Modal.getInstance(this.$refs.modal)?.dispose();
  },
  methods: {
    money(n) {
      const amount = Number(n || 0);
      return (amount < 0 ? "-$" : "$") + Math.abs(amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    // step 1 = Additional Items, 2 = Finalize; the order and its items are read fresh (the summary may have changed them)
    async open(step) {
      const [detail, list] = await Promise.all([rqApi.detail(this.orderId), rqApi.items(this.orderId)]);
      if (!detail.ok) {
        toast.error(detail.message || "Replacement quote not found.");
        return;
      }
      this.order = detail.body;
      const items = list.ok && Array.isArray(list.body) ? list.body : (this.order.items || []);
      this.items = items.map(i => ({ id: i.id || 0, title: i.title, price: i.price, quantity: i.quantity || 1 }));
      this.itemErrors = [];
      this.paymentTypes = [];
      this.payKey = "";
      this.step = 1;
      this.backToFinalize = false;
      if (step === 2 && !(await this.finalize())) return;
      disableScroll();
      Modal.getOrCreateInstance(this.$refs.modal, { backdrop: "static", keyboard: false }).show();
    },
    err(idx, field) {
      return this.itemErrors[idx]?.[field] || "";
    },
    clearErr(idx, field) {
      if (this.itemErrors[idx]?.[field]) this.itemErrors[idx] = { ...this.itemErrors[idx], [field]: "" };
    },
    removeItem(idx) {
      this.items.splice(idx, 1);
      this.itemErrors.splice(idx, 1);
    },
    // every row on the form needs Title, Price and Quantity (remove a row with its trash button)
    validateItems() {
      this.itemErrors = this.items.map(i => {
        const price = String(i.price ?? "").trim();
        const qty = Number(i.quantity);
        return {
          title: String(i.title ?? "").trim() ? "" : "Title is required",
          price: price === "" ? "Price is required" : (!/^\d+(\.\d{1,2})?$/.test(price) ? "Enter a valid price" : ""),
          quantity: !Number.isInteger(qty) || qty < 1 || qty > 99 ? "Quantity must be 1 to 99" : "",
        };
      });
      return !this.itemErrors.some(e => e.title || e.price || e.quantity);
    },
    backFromItems() {
      this.backToFinalize = false;
      this.itemErrors = [];
      this.step = 2;
    },
    editItemsFromFinalize() {
      this.backToFinalize = true;
      this.step = 1;
    },
    close() {
      Modal.getInstance(this.$refs.modal)?.hide();
      enableScroll();
    },
    // Android OrderAddAddtionalItemsFragment "Save Items": back to the summary page
    async saveItems() {
      if (!this.validateItems()) return;
      const items = this.items;
      this.busy = true;
      const payload = items.map(i => ({ id: i.id || 0, title: String(i.title).trim(), price: Number(i.price), quantity: Number(i.quantity) }));
      const res = await rqApi.saveItems(this.orderId, payload);
      this.busy = false;
      if (!res.ok) {
        toast.error(res.message || "Something went wrong.");
        return;
      }
      toast.success(res.message || "Items saved.");
      this.$emit("saved");
      if (this.backToFinalize) {
        // the totals changed with the items: read the order again, then show Finalize
        this.backToFinalize = false;
        // items/submit recreates the rows (new ids) and inspection/submit looks the ids up: read both again
        const [fresh, list] = await Promise.all([rqApi.detail(this.orderId), rqApi.items(this.orderId)]);
        if (fresh.ok && fresh.body?.id) this.order = fresh.body;
        this.items = list.ok && Array.isArray(list.body)
          ? list.body.map(i => ({ id: i.id || 0, title: i.title, price: i.price, quantity: i.quantity || 1 }))
          : payload.map(i => ({ ...i, id: 0 }));
        await this.finalize();
        return;
      }
      this.close();
    },
    // Android "Finalize the quote": totals must be positive; a preferred payment type (or a booking order) skips the choice
    async finalize() {
      if (!(Number(this.order.total) + Number(this.order.parts_total || 0) > 0)) {
        toast.error("The total amount must not be in negative. Please enter correct amount for Additional Items");
        return false;
      }
      this.step = 2;
      const preferred = this.order.preferred_payment_type || {};
      this.skipPayment = !!this.order.is_from_booking || !!(preferred.type || preferred.sub_type);
      if (this.skipPayment) return true;
      const types = await rqApi.paymentTypes(this.orderId);
      this.paymentTypes = types.ok && Array.isArray(types.body) ? types.body : [];
      const match = this.paymentOptions.find(o => o.type === preferred.type && (o.subType || null) === (preferred.sub_type || null));
      if (match) this.payKey = match.key;
      else if (this.order.is_from_booking) this.payKey = this.paymentOptions.find(o => o.type === "other" && o.subType === "other")?.key || "";
      return true;
    },
    submit() {
      let type = null;
      let subType = null;
      if (this.order.is_from_booking) {
        type = "other";
        subType = "other";
      } else if (!this.skipPayment) {
        const opt = this.paymentOptions.find(o => o.key === this.payKey);
        if (!opt) return toast.error("Select PaymentType method first");
        type = opt.type;
        subType = opt.subType;
      }
      this.$dialog.show({
        title: "Submit inspection",
        text: "Are you sure you want to submit the PerfectMatch inspection report?",
        buttonText: "Yes",
        onConfirm: async () => {
          this.busy = true;
          const res = await rqApi.inspectionSubmit(this.orderId, this.items, type, subType);
          this.busy = false;
          if (!res.ok) {
            toast.error(res.message || "Something went wrong.");
            return;
          }
          toast.success("PerfectMatch report submitted successfully.");
          this.close();
          this.$emit("submitted", res);
        },
      });
    },
  },
};
</script>

<style scoped>
.rqFinalSection {
  padding: 12px 15px;
  border-radius: 10px;
  box-shadow: 0px 0px 10px 0px #0000001A;
}

.rqFinalTitle {
  margin: 0 0 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #EFEFEF;
  font-size: 15px;
  font-weight: 500;
  color: #8479E1;
}

.rqFinalPrice {
  padding: 0;
  border: 0;
  background: transparent;
  font-size: 15px;
  font-weight: 500;
  color: #7695FF;
  text-decoration: underline;
}

.rqInput {
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #E2E5EC;
  border-radius: 8px;
  font-size: 14px;
  color: #333333;
  outline: none;
  background: #fff;
}

.rqInput:focus {
  border-color: #7695FF;
}

.rqInputError,
.rqInputError:focus {
  border-color: #EE5851;
}

.rqFieldError {
  margin: 4px 0 0;
  font-size: 12px;
  color: #EE5851;
}
</style>
